import { Group } from "fabric";
import intl from "react-intl-universal";
import ixusRest from "../../rest/ixusRest";
import plotUtils from "./plotUtils";
import safetyDistanceUtils from "./safetyDistanceUtils";
// Cropped from the customer-supplied Cloud.jpg and Peter Schneider's bolder Richtfunksymbol.jpg
// redraw (ticket attachments), background removed and downscaled (service-desk#1617).
import cloud from "../../asset/ixus/cloud.png";
import dish from "../../asset/ixus/dish.png";

// Shared between placing a new climbing square (IxusSiteInfoLayer) and redrawing a saved
// one (drawTextFields below), so the look never drifts apart (service-desk#1617).
const climbingSquareStyle = {
    fill: "transparent",
    stroke: "blue",
    strokeWidth: 2,
    strokeUniform: true,
    strokeDashArray: [6, 4],
};

const drawPlan = async (plan, ixusAreas, plot, layer) => {
    const opacityBackup = plan.opacity;
    plan.opacity = 10;
    const image = await plotUtils.addPlan(0, plan, layer, plot);
    image.set({
        selectable: false,
        evented: false,
        hasControls: false,
        nonInteractive: true,
    });
    plan.opacity = opacityBackup;
};

const drawResult = async (ixusArea, imageBase64, plot, layer) => {
    const { calculation } = ixusArea;
    const {
        bottomRightX, bottomRightY, imageHeight,
        imageWidth, topLeftX, topLeftY, cutLevelMin, cutLevelMax
    } = calculation.ixusResult;

    const xMeters = Math.abs(bottomRightX - topLeftX);
    const yMeters = Math.abs(topLeftY - bottomRightY);
    const cMeters = Math.abs(cutLevelMax - cutLevelMin);


    const minX = Math.min(topLeftX, bottomRightX);
    const minY = Math.min(topLeftY, bottomRightY);
    const scaleX = xMeters / plot.refScale / imageWidth;
    const scaleY = (ixusArea.sideView ? cMeters : yMeters) / plot.refScale / imageHeight;

    // const heightMeters = Math.abs(cutLevelMax - cutLevelMin);

    const left = minX / plot.refScale;

    const top = 0 - ((ixusArea.sideView ? cutLevelMin : minY) / plot.refScale);

    await plot.addImage(layer, left, top, 0, 1, 0.5, imageBase64, {
        calculationId: calculation.id,
        scaleX: scaleX,
        scaleY: scaleY,
        // Computed placement, kept so the user can read off how far the image was dragged or
        // resized afterwards (IxusResultTransformDialog).
        originalLeft: left,
        originalTop: top,
        originalScaleX: scaleX,
        originalScaleY: scaleY,
        originX: "left",
        originY: "bottom",
        imageSmoothing: false,
        selectable: true,
        evented: true,
        hasControls: true,
        //border
        stroke: "#111",
        strokeWidth: 2,
        strokeUniform: true,
        mxwellname: "ixusArea",
    });
};

const getNonTransparentBounds = imageBase64 => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
        const { width, height } = img;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, width, height);

        let minX = width, minY = height, maxX = -1, maxY = -1;
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                if (data[(y * width + x) * 4 + 3] !== 0) {
                    if (x < minX) {
                        minX = x;
                    }
                    if (x > maxX) {
                        maxX = x;
                    }
                    if (y < minY) {
                        minY = y;
                    }
                    if (y > maxY) {
                        maxY = y;
                    }
                }
            }
        }

        if (maxX < 0) {
            resolve(null);
        } else {
            resolve({ minX, minY, maxX: maxX + 1, maxY: maxY + 1, width, height });
        }
    };
    img.onerror = reject;
    img.src = imageBase64.startsWith("data:") ? imageBase64 : `data:image/png;base64,${imageBase64}`;
});

const drawInvisibleResultBounds = async (ixusArea, imageBase64, plot, layer) => {
    const bounds = await getNonTransparentBounds(imageBase64);
    if (!bounds) {
        return null;
    }

    const { calculation } = ixusArea;
    const { bottomRightX, bottomRightY, imageHeight, imageWidth, topLeftX, topLeftY,
        cutLevelMin, cutLevelMax } = calculation.ixusResult;

    const xMeters = Math.abs(bottomRightX - topLeftX);
    const yMeters = Math.abs(topLeftY - bottomRightY);
    const cMeters = Math.abs(cutLevelMax - cutLevelMin);

    const scaleX = xMeters / plot.refScale / imageWidth;
    const scaleY = (ixusArea.sideView ? cMeters : yMeters) / plot.refScale / imageHeight;

    const minX = Math.min(topLeftX, bottomRightX);
    const minY = Math.min(topLeftY, bottomRightY);
    const imageLeft = minX / plot.refScale;
    const imageBottom = 0 - ((ixusArea.sideView ? cutLevelMin : minY) / plot.refScale);
    const imageTop = imageBottom - imageHeight * scaleY;

    const rectLeft = imageLeft + bounds.minX * scaleX;
    const rectTop = imageTop + bounds.minY * scaleY;
    const rectWidth = (bounds.maxX - bounds.minX) * scaleX;
    const rectHeight = (bounds.maxY - bounds.minY) * scaleY;

    return plot.addRect(layer, rectLeft, rectTop, rectWidth, rectHeight, false, false, {
        fill: "transparent",
        stroke: null,
        strokeWidth: 0,
        selectable: false,
        hasControls: false,
        nonInteractive: true,
        calculationId: calculation.id,
        mxwellname: "ixusAreaBounds",
    });
};

const addMeasurement = (plot, layer, p1, p2, direction, levelPlanKey) => {
    const scale = plot.refScale;
    const x1 = p1.x / scale, y1 = p1.y / scale;
    const x2 = p2.x / scale, y2 = p2.y / scale;
    const line = plot.addLine(layer, x1, y1, x2, y2, true, { stroke: "black" });
    const start = plot.addRect(layer, x1, y1, 2, 12, false, true, { fill: "black", originX: "center", originY: "center" });
    const end = plot.addRect(layer, x2, y2, 2, 12, false, true, { fill: "black", originX: "center", originY: "center" });

    const diffX = x2 - x1;
    const diffY = y2 - y1;
    const distance = Math.hypot(diffX, diffY) * scale;
    const textStr = distance.toFixed(2) + "m";
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const text = plot.addText(layer, cx, cy, true, { fill: "black", originX: "center" });
    text.set({ text: textStr });

    const angleRad = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
    const flipText = angleRad < -90 || angleRad > 90;
    text.set({ flipY: flipText, flipX: flipText });
    [text, start, end].forEach(obj => obj.rotate(angleRad));

    line.isOnScreen = () => true;
    start.isOnScreen = () => true;
    end.isOnScreen = () => true;

    const group = new Group([line, start, end, text]);
    layer.addObject(group);
    group.set({ feature: "rect-feature", direction, levelPlanKey });
};

// Redraws a user-drawn arrow (service-desk#1668) from its two saved endpoints, the same way
// addMeasurement rebuilds a measurement line - style matches the north-direction arrow drawn
// via plot.applyLineFeature(..., true) (PlanWizard.js), just without the distance label.
// The red line (service-desk#1694) is redrawn the same way, but red and with end ticks
// instead of an arrow head.
const addLineSymbol = (plot, layer, p1, p2, kind) => {
    const isArrow = kind === safetyDistanceUtils.arrowKind;
    const scale = plot.refScale;
    const x1 = p1.x / scale, y1 = p1.y / scale;
    const x2 = p2.x / scale, y2 = p2.y / scale;
    const color = isArrow ? "blue" : "red";
    const line = plot.addLine(layer, x1, y1, x2, y2, true, { stroke: color });
    // The arrow has no start tick, as in applyLineFeature - it is kept hidden to keep the group layout.
    const start = plot.addRect(layer, x1, y1, 2, 12, false, true, { fill: color, originX: "center", originY: "center", opacity: isArrow ? 0 : 1 });
    const end = isArrow
        ? plot.addTriangle(layer, x2, y2, 8, 12, false, true, { fill: color, originX: "center", originY: "center" })
        : plot.addRect(layer, x2, y2, 2, 12, false, true, { fill: color, originX: "center", originY: "center" });

    const angleDeg = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
    [start, end].forEach(obj => obj.rotate(angleDeg + (obj === end && isArrow ? 90 : 0)));

    line.isOnScreen = () => true;
    start.isOnScreen = () => true;
    end.isOnScreen = () => true;

    const group = new Group([line, start, end]);
    layer.addObject(group);
    group.set({ feature: "placement-feature", kind, hasControls: true, perPixelTargetFind: true });
};

const drawMeasurments = (plan, plot, layer, ixusAreas) => {
    for (const ixusArea of ixusAreas) {
        if (ixusArea.calculation
                    && ixusArea.planKey === plan.key) {
            const {measurements} = ixusArea.calculation.ixusResult;
            if(measurements) {
                for(const measurment of measurements) {
                    const {points, direction, levelPlanKey} = safetyDistanceUtils.toMeasurement(measurment);
                    const [p1, p2] = points;
                    addMeasurement(plot, layer, p1, p2, direction, levelPlanKey);
                }
            }
        }
    }
};

const drawSafetyDistances = async (textField, plot, layer, ixusAreas, plans, siteType) => {
    const scale = plot.refScale;
    const {levelPlanKey, left, top, scaleX, scaleY, angle} = textField;
    const levels = safetyDistanceUtils.getLevels(ixusAreas, plans);
    if (!levels.some(level => level.planKey === levelPlanKey)) {
        return;
    }
    const levelArea = ixusAreas.find(ixusArea => ixusArea.planKey === levelPlanKey);
    const content = safetyDistanceUtils.buildContent(levelPlanKey, ixusAreas, levelArea?.distanceNote, siteType);
    await plot.addTableTextbox(layer, left / scale, top / scale, content, {
        scaleX,
        scaleY,
        angle,
        feature: "placement-feature",
        kind: safetyDistanceUtils.kind,
        levelPlanKey,
    });
};

// Draws the plain text boxes, the safety distance table (with its automatic warning
// triangle) and the placed symbols (cloud, directional radio, climbing square, arrow) of a
// level (service-desk#1617). siteType controls whether the warning triangle is shown at all.
const drawTextFields = async (plan, plot, layer, ixusAreas, plans, siteType) => {
    const scale = plot.refScale;
    for (const ixusArea of ixusAreas) {
        if (!ixusArea.calculation || ixusArea.planKey !== plan.key) {
            continue;
        }
        const {textFields} = ixusArea.calculation.ixusResult;
        for (const textField of textFields || []) {
            const {kind, left, top, scaleX, scaleY, angle} = textField;
            const x = left / scale;
            const y = top / scale;
            const placement = {scaleX, scaleY, angle, feature: "placement-feature", kind};
            if (kind === safetyDistanceUtils.kind) {
                await drawSafetyDistances(textField, plot, layer, ixusAreas, plans, siteType);
            } else if (kind === safetyDistanceUtils.cloudKind) {
                await plot.addImage(layer, x, y, angle, 1, safetyDistanceUtils.cloudOpacity, cloud, {
                    ...placement,
                    originX: "center",
                    originY: "center",
                    hasControls: true,
                });
            } else if (kind === safetyDistanceUtils.directionalRadioKind) {
                await plot.addIconTextbox(layer, x, y, dish,
                    intl.get("entry.ixus.textFields.directionalRadioCaption"), placement);
            } else if (kind === safetyDistanceUtils.climbingSquareKind) {
                const size = safetyDistanceUtils.climbingSquareSizeMeters / scale;
                const rect = plot.addRect(layer, x, y, size, size, true, false, {
                    ...placement,
                    ...climbingSquareStyle,
                    originX: "center",
                    originY: "center",
                });
                rect.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false });
            } else if (kind === safetyDistanceUtils.arrowKind || kind === safetyDistanceUtils.redLineKind) {
                const [p1, p2] = textField.points;
                addLineSymbol(plot, layer, p1, p2, kind);
            } else {
                const {text, width} = textField;
                plot.addTextbox(layer, x, y, text, {
                    ...placement,
                    width: width / scale,
                });
            }
        }
    }
};

const drawPlanAndResult = async (plan, ixusAreas, plot, viewLayer, measurmentLayer) => {
    await drawPlan(plan, ixusAreas, plot, viewLayer);
    for (const ixusArea of ixusAreas) {
        if (ixusArea.calculation?.ixusResult
                && ixusArea.planKey === plan.key) {
            const imageBase64 = await ixusRest.getImage(ixusArea.calculation.id);
            await drawInvisibleResultBounds(ixusArea, imageBase64, plot, viewLayer);
            await drawResult(ixusArea, imageBase64, plot, viewLayer);
        }
    }
    if (measurmentLayer) {
        drawMeasurments(plan, plot, measurmentLayer, ixusAreas);
    }
};

const ixusUtils = {drawPlan, drawPlanAndResult, drawMeasurments, drawTextFields, climbingSquareStyle};
export default ixusUtils;